import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  useTheme,
  Button,
  TextField,
} from "@mui/material";
import { GET, POST, PUT } from "../Generic/CrudRequest";
import Icons from "../../Utils/Icons";
import {
  InpectionPointEdit,
  InpectionPointAdd,
} from "./InspectionPointAddOrEdit";

export default function AddOrEditClient({ config }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [inpectionPointData, setInpectionPointData] = useState(
    new InpectionPointAdd()
  );
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (location.pathname.includes("/PuntosInspeccion/Editar")) {
      setTitle("edit");
      const inpectionPointId = getInpectionPointIdFromPath(location.pathname);
      getdataInpectionPointById(inpectionPointId);
    } else {
      setTitle("add");
    }
  }, [location.pathname]);

  const getInpectionPointIdFromPath = (pathname) => {
    const parts = pathname.split("/");
    return parts[parts.length - 1];
  };

  const getdataInpectionPointById = async (InpectionPointId) => {
    const dataInpectionPoint = await GET(
      `${config.url}/api/InpectionPoint/${InpectionPointId}`
    );
    const updateDataInpectionPoint = new InpectionPointEdit(dataInpectionPoint);
    setInpectionPointData(updateDataInpectionPoint); //TODO: revisar como lo esta trayendo el nuevo endpoint 
  };

  const addInpectionPoint = async () => {
    const requiredFields = ["descripcion", "descripcionIngles"] ;
    const newErrors = {};

    requiredFields.forEach((field) => {
      const fieldValue = getFieldByPath(inpectionPointData, field);
      if (!fieldValue || fieldValue.trim() === "") {
        newErrors[field] = t("inspectionPoint.addOrEdit.requiredField");
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    await POST(`${config.url}/api/InpectionPoint`, inpectionPointData); //TODO: revisar como lo esta mandando en el nuevo endpoint
    navigate("/PuntosInspeccion");
  };

  const editInpectionPoint = async () => {
    const requiredFields = ["descripcion", "descripcionIngles"] ;
    const newErrors = {};

    requiredFields.forEach((field) => {
      const fieldValue = getFieldByPath(inpectionPointData, field);
      if (!fieldValue || fieldValue.trim() === "") {
        newErrors[field] = t("inspectionPoint.addOrEdit.requiredField");
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    await PUT(`${config.url}/api/InpectionPoint`, inpectionPointData);
    navigate("/PuntosInspeccion");
  };

  const getFieldByPath = (object, path) => {
    const pathArray = path.split(".");
    let value = object;
    for (const prop of pathArray) {
      value = value[prop];
      if (value === undefined) break;
    }
    return value;
  };

  const handleSubmit = async () => {
    if (title === "add") {
      await addInpectionPoint();
    } else if (title === "edit") {
      await editInpectionPoint();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInpectionPointData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Determine current language
  const isEnglish = i18n.language === "en";

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
      }}
    >
      <CardContent>
        <CardContent
          sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
        >
          <Icons
            iconName="ArrowBack"
            fontSize="large"
            style={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              marginRight: 20,
            }}
            to="/PuntosInspeccion"
            tooltip={t("buttons.back")}
          />
          <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
            {t(`inspectionPoint.addOrEdit.${title}`)}{" "}
            {t("inspectionPoint.addOrEdit.title")}
          </Typography>
        </CardContent>
        <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
        <Card
          variant="outlined"
          sx={{
            overflow: "hidden",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(0, 0, 0, 0.1)",
            borderRadius: 2,
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
            marginTop: 2,
          }}
        >
          <CardContent sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }}>
            <Typography
              variant="h5"
              sx={{
                flex: "1",
                color: theme.palette.primary.main,
              }}
            >
              {t("inspectionPoint.addOrEdit.inspectionPointInfo")}
            </Typography>
            <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
            <Grid container spacing={2} sx={{ marginTop: 2 }}>
            {isEnglish ? 
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={"English Description"}
                  name="descripcionIngles"
                  value={inpectionPointData.descripcionIngles || ""}
                  variant="standard"
                  onChange={handleChange}
                  error={!!errors.descripcionIngles}
                  helperText={errors.descripcionIngles}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={"Spanish Description"}
                  name="descripcion"
                  value={inpectionPointData.descripcion || ""}
                  variant="standard"
                  onChange={handleChange}
                  error={!!errors.descripcion}
                  helperText={errors.descripcion}
                />
              </Grid>
            </>
            :
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={"Descripción en Español"}
                  name="descripcion"
                  value={inpectionPointData.descripcion || ""}
                  variant="standard"
                  onChange={handleChange}
                  error={!!errors.descripcion}
                  helperText={errors.descripcion}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={"Descripcion en Inglés"}
                  name="descripcionIngles"
                  value={inpectionPointData.descripcionIngles || ""}
                  variant="standard"
                  onChange={handleChange}
                  error={!!errors.descripcionIngles}
                  helperText={errors.descripcionIngles}
                />
              </Grid>
            </>            
            }
              <Grid container justifyContent="flex-end" sx={{ marginTop: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                >
                  {t("inspectionPoint.addOrEdit.save")}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
}
